import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import ExamCard from '../../components/User/ExamCard';
import AddExamModal from '../../components/User/AddExamModal';
import { fetchExams, createExam, updateExam, deleteExam } from '../../services/api'; 

const BoardHub = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [examToEdit, setExamToEdit] = useState(null); // ✅ Added state to track which exam is being edited
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  
  const openCreateModal = () => {
    setExamToEdit(null);
    setIsModalOpen(true);
  };

  const openEditModal = (exam) => {
    setExamToEdit(exam);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setExamToEdit(null);
  };

  // --- 1. FETCH EXAMS ON LOAD ---
  useEffect(() => {
    const loadExams = async () => {
      try {
        setLoading(true);
        const data = await fetchExams(); 
        setExams(data);
        setError(null);
      } catch (err) {
        console.error("Failed to load exams", err);
        setError("Could not load your exams. Please check your connection.");
      } finally {
        setLoading(false);
      }
    };
    loadExams();
  }, []);

  // --- 2. HANDLE SAVE (CREATE OR EDIT) ---
  const handleSaveExam = async (formData) => {
    try {
      if (examToEdit) {
        // Edit existing
        const updatedExam = await updateExam(examToEdit._id, formData);
        setExams(prev => prev.map(exam => exam._id === examToEdit._id ? updatedExam : exam));
        Swal.fire("Updated!", "Exam deck updated successfully.", "success");
      } else {
        // Create new
        const newExam = await createExam(formData);
        setExams(prev => [newExam, ...prev]);
        Swal.fire("Created!", "New exam deck created successfully.", "success");
      }
    } catch (err) {
      console.error("Failed to save exam", err);
      Swal.fire("Error", "Failed to save exam. Please try again.", "error");
    } finally {
      closeModal();
    }
  };

  // --- 3. HANDLE DELETE ---
  const handleDeleteExam = async (examId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#94A3B8",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteExam(examId);
          setExams(prev => prev.filter(exam => exam._id !== examId));
          Swal.fire("Deleted!", "Your exam deck has been deleted.", "success");
        } catch (err) {
          console.error("Failed to delete exam", err);
          Swal.fire("Error", "Failed to delete exam.", "error");
        }
      }
    });
  };

  return (
    <div className="bg-slate-50 text-slate-800 font-sans antialiased flex h-screen overflow-hidden">
      
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <main className="flex-1 flex flex-col h-full overflow-hidden relative">
        <Header toggleSidebar={toggleSidebar} title="Exam Prep Hub" />

        <div className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-7xl mx-auto w-full animate-fade-in pb-20 md:pb-0">
            
            {/* HERO BANNER */}
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-blue-700 to-blue-600 p-8 text-white shadow-xl shadow-blue-900/20 mb-8 group">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -mr-10 -mt-10 blur-3xl group-hover:opacity-10 transition duration-700"></div>
              <div className="absolute bottom-0 left-0 w-40 h-40 bg-blue-400 opacity-10 rounded-full -ml-10 -mb-10 blur-2xl"></div>
              
              <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div className="max-w-2xl">
                   <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-[10px] font-bold uppercase tracking-wider mb-3 text-blue-100 border border-white/10">
                      <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                      Exam Season Ready
                   </div>
                   <h2 className="text-2xl md:text-3xl font-bold mb-2">Your Path to Mastery</h2>
                   <p className="text-blue-100 text-sm md:text-base leading-relaxed opacity-90 max-w-xl">
                      Access specialized reviewers, mock exams, and resources designed for all students.
                   </p>
                </div>
                <div className="hidden md:block transform group-hover:scale-110 transition duration-500">
                    <div className="w-20 h-20 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center border border-white/20 shadow-2xl rotate-3">
                        <i className="fa-solid fa-shapes text-4xl text-white/90"></i>
                    </div>
                </div>
              </div>
            </div>

            {/* --- CONTENT GRID --- */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="h-[280px] bg-slate-200 rounded-3xl animate-pulse"></div>
                <div className="h-[280px] bg-slate-200 rounded-3xl animate-pulse"></div>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                 <i className="fa-solid fa-triangle-exclamation text-4xl mb-3"></i>
                 <p>{error}</p>
                 <button onClick={() => window.location.reload()} className="mt-4 text-blue-600 font-bold hover:underline">Retry</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
                
                {/* 1. DYNAMIC EXAM CARDS */}
                {exams.map((exam) => (
                  <ExamCard 
                    key={exam._id}         
                    id={exam._id}          
                    title={exam.subjectCode}
                    subtitle={exam.title}
                    color={exam.appearance?.color || 'blue'}
                    icon={exam.appearance?.icon || 'fa-book-open'} 
                    tag={exam.category} 
                    updated={new Date(exam.updatedAt).toLocaleDateString()}
                    desc={exam.description}
                    resources={`${exam.resourceCount || 0} Resources`}
                    isPrivate={false} 
                    examObj={exam} // ✅ Pass full object for editing
                    onEdit={() => openEditModal(exam)}
                    onDelete={() => handleDeleteExam(exam._id)}
                  />
                ))}

                {/* 2. CREATE NEW CARD BUTTON */}
                <div 
                  onClick={openCreateModal}
                  className="group relative cursor-pointer overflow-hidden rounded-3xl border-2 border-dashed border-slate-300 bg-slate-50/50 p-6 flex flex-col items-center justify-center text-center hover:border-blue-500 hover:bg-blue-50/30 transition-all duration-300 min-h-[280px]"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-blue-500/0 group-hover:from-blue-500/5 group-hover:to-indigo-500/5 transition-all duration-500"></div>
                  
                  <div className="relative z-10 flex flex-col items-center">
                    <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-sm border border-slate-200 group-hover:scale-110 group-hover:shadow-md group-hover:border-blue-200 transition-all duration-300">
                      <i className="fa-solid fa-plus text-2xl text-slate-400 group-hover:text-blue-600 transition-colors"></i>
                    </div>
                    
                    <h3 className="text-lg font-bold text-slate-700 group-hover:text-blue-700 transition-colors">
                      Create New Exam Deck
                    </h3>
                    
                    <p className="mt-2 max-w-xs text-xs text-slate-500 leading-relaxed">
                      Preparing for something else? Build a custom reviewer or browse our official catalog.
                    </p>

                    <span className="mt-6 inline-flex items-center text-xs font-bold text-blue-600 opacity-0 transform translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                      Get Started <i className="fa-solid fa-arrow-right ml-1"></i>
                    </span>
                  </div>
                </div>

              </div>
            )}
          </div>
        </div>
      </main>

      {/* MODAL */}
      {isModalOpen && (
        <AddExamModal 
          examToEdit={examToEdit} // ✅ Pass the exam if editing
          onClose={closeModal} 
          onSave={handleSaveExam} 
        />
      )}
    </div>
  );
};

export default BoardHub;
